import React, { useEffect, useRef, useState } from 'react';
import './inboundReceivedQtyInput.css';

export const isValidReceivedQty = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return false;
  }
  const num = Number(trimmed);
  return Number.isSafeInteger(num) && num >= 0;
};

/** The next line's count box, in the order the lines are listed; nothing when this was the last. */
const nextQtyInput = (from: HTMLInputElement): HTMLInputElement | undefined => {
  const scope = from.closest('table') ?? document;
  const boxes = Array.from(scope.querySelectorAll<HTMLInputElement>('input.qtyin')).filter((box) => !box.disabled);
  return boxes[boxes.indexOf(from) + 1];
};

export interface InboundReceivedQtyInputProps {
  id: string;
  sku: string;
  lineNo: number;
  initialQty: number;
  disabled?: boolean;
  /** Returns whether the count reached the server; refresh failure still counts as saved. */
  onSave: (qty: number) => Promise<boolean>;
  onErrorChange: (lineNo: number, hasError: boolean) => void;
}

/**
 * One line's count, typed. Leaving the box saves it; so does Enter, which then moves the cursor to the next
 * line so a whole request is typed top to bottom without reaching for the mouse. Escape puts the saved
 * count back. A count the server refused is put back too.
 */
export function InboundReceivedQtyInput({
  id,
  sku,
  lineNo,
  initialQty,
  disabled,
  onSave,
  onErrorChange,
}: InboundReceivedQtyInputProps) {
  const [value, setValue] = useState(String(initialQty));
  const [error, setError] = useState<string | null>(null);
  /** The text being saved right now: Enter saves and then moves the cursor, and the blur that causes must not save it again. */
  const inFlight = useRef<string | null>(null);
  /**
   * Locked while its count is on the wire. A second figure typed into the line before the first landed
   * would be sent against the total the desk last read, and the two differences would add up wrong.
   */
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(initialQty));
    setError(null);
    onErrorChange(lineNo, false);
  }, [initialQty, lineNo, onErrorChange]);

  /** Validates, then saves a changed count; returns whether the text was a count at all. */
  const commit = async (): Promise<boolean> => {
    if (!isValidReceivedQty(value)) {
      const msg = 'Enter a whole number of 0 or more.';
      setError(msg);
      onErrorChange(lineNo, true);
      return false;
    }
    setError(null);
    onErrorChange(lineNo, false);
    const parsed = Number(value.trim());
    if (parsed === initialQty || inFlight.current === value) {
      return true;
    }
    inFlight.current = value;
    setSaving(true);
    try {
      const saved = await onSave(parsed);
      if (!saved) {
        setValue(String(initialQty));
      }
    } finally {
      inFlight.current = null;
      setSaving(false);
    }
    return true;
  };

  const handleBlur = () => {
    void commit();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setValue(next);
    if (error && isValidReceivedQty(next)) {
      setError(null);
      onErrorChange(lineNo, false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setValue(String(initialQty));
      setError(null);
      onErrorChange(lineNo, false);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const box = event.currentTarget;
      void commit();
      if (!isValidReceivedQty(value)) {
        return; // The error is on screen; the cursor stays on the line that needs fixing.
      }
      const next = nextQtyInput(box);
      if (next) {
        next.focus();
        next.select();
      } else {
        // The last line: the cursor leaves the lines, and the desk's own rule returns it to the scan box.
        box.blur();
      }
    }
  };

  return (
    <div className="qty-wrap">
      <label className="visually-hidden" htmlFor={id}>
        {sku} received
      </label>
      <input
        id={id}
        className={`qtyin${error ? ' is-invalid err' : ''}`}
        value={value}
        disabled={disabled || saving}
        inputMode="numeric"
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <div id={`${id}-error`} className="invalid-feedback text-danger" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
